import { useContext } from "react";
import { Context } from "../../../../context/Context";
import { Button, CircleX } from "../../../ui";

const ACTION_LABELS = {
  create: { ar: "إضافة", en: "Create" },
  update: { ar: "تعديل", en: "Update" },
  // Legacy records only (written before the approval flags were removed).
  approve: { ar: "موافقة (قديم)", en: "Approved (legacy)" },
  reject: { ar: "رفض (قديم)", en: "Rejected (legacy)" },
  hide: { ar: "إخفاء", en: "Hide" },
  show: { ar: "إظهار", en: "Show" },
  delete: { ar: "حذف", en: "Delete" },
  image: { ar: "تغيير الصورة", en: "Image update" },
  reorder: { ar: "إعادة ترتيب", en: "Reorder" },
  link_additions: { ar: "ربط الإضافات", en: "Link additions" },
  menu_create: { ar: "إنشاء قائمة الطعام", en: "Menu created" },
  menu_duplicate: { ar: "نسخ قائمة الطعام", en: "Menu duplicated" },
  menu_update: { ar: "تعديل بيانات قائمة الطعام", en: "Menu details updated" },
  menu_activate: { ar: "اعتماد قائمة الطعام", en: "Menu activated" },
  menu_archive: { ar: "أرشفة قائمة الطعام", en: "Menu archived" },
  menu_restore: { ar: "استعادة قائمة الطعام", en: "Menu restored" },
  section_restore: { ar: "استعادة قسم", en: "Section restored" },
  item_restore: { ar: "استعادة صنف", en: "Item restored" },
  item_move: { ar: "نقل صنف لقسم آخر", en: "Item moved to another section" },
  item_copy: { ar: "نسخ صنف", en: "Item copied" },
  section_copy: { ar: "نسخ قسم", en: "Section copied" },
  discount_bulk: { ar: "تعديل خصومات جماعي", en: "Bulk discount change" },
  addition_restore: { ar: "استعادة مجموعة إضافات", en: "Addition restored" },
  section_create: { ar: "إضافة قسم", en: "Section created" },
  section_update: { ar: "تعديل قسم", en: "Section updated" },
  section_archive: { ar: "أرشفة قسم", en: "Section archived" },
  item_create: { ar: "إضافة صنف", en: "Item created" },
  item_update: { ar: "تعديل صنف", en: "Item updated" },
  item_archive: { ar: "أرشفة صنف", en: "Item archived" },
  pricing: { ar: "تعديل الأسعار", en: "Prices changed" },
  addition_create: { ar: "إضافة مجموعة إضافات", en: "Addition created" },
  addition_update: { ar: "تعديل مجموعة إضافات", en: "Addition updated" },
  addition_archive: { ar: "أرشفة مجموعة إضافات", en: "Addition archived" },
  branch_assign: { ar: "ربط فرع", en: "Branch assigned" },
  branch_unassign: { ar: "إلغاء ربط فرع", en: "Branch removed" },
  branch_hide: { ar: "إخفاء في فرع", en: "Hidden in branch" },
  branch_show: { ar: "إظهار في فرع", en: "Shown in branch" },
  import: { ar: "استيراد ملف", en: "Spreadsheet import" },
  section_reorder: { ar: "ترتيب الأقسام", en: "Sections reordered" },
  item_reorder: { ar: "ترتيب الأصناف", en: "Items reordered" },
  addition_reorder: { ar: "ترتيب الإضافات", en: "Additions reordered" },
  // Record written by the data migration: the item's old approval flags.
  legacy_state: { ar: "حالة قديمة (قبل التحديث)", en: "Legacy state (before update)" },
};

const SIDE_NAMES = { lud: { ar: "فريق لُد", en: "LUD team" }, restaurant: { ar: "المطعم", en: "Restaurant" } };

const FIELD_LABELS = {
  name: { ar: "الاسم", en: "Name" },
  description: { ar: "الوصف", en: "Description" },
  allergens: { ar: "مسببات الحساسية", en: "Allergens" },
  calories: { ar: "السعرات", en: "Calories" },
  addCosts: { ar: "الأسعار", en: "Prices" },
  additionGroups: { ar: "مجموعات الإضافات", en: "Addition groups" },
  addition: { ar: "الإضافة", en: "Addition" },
  order: { ar: "الترتيب", en: "Order" },
  // Fields that only appear in legacy records.
  isApproved: { ar: "الاعتماد (قديم)", en: "Approval (legacy)" },
  status: { ar: "الحالة", en: "Status" },
  rejectReason: { ar: "سبب الرفض (قديم)", en: "Rejection reason (legacy)" },
  isShow: { ar: "حالة الظهور", en: "Visibility" },
  hiddenByStore: { ar: "إخفاء المتجر", en: "Hidden by store" },
  hiddenBranches: { ar: "الفروع التي أخفت الصنف", en: "Branches hiding the item" },
  image: { ar: "الصورة", en: "Image" },
  isBox: { ar: "نوع الصنف", en: "Item type" },
  ar: { ar: "العربية", en: "Arabic" },
  en: { ar: "الإنجليزية", en: "English" },
  cost: { ar: "السعر", en: "Price" },
  discount: { ar: "الخصم", en: "Discount" },
  branchId: { ar: "معرف الفرع", en: "Branch ID" },
  branchName: { ar: "اسم الفرع", en: "Branch name" },
  // Current model (legacy labels above stay for old records).
  pricing: { ar: "الأسعار", en: "Prices" },
  price: { ar: "السعر", en: "Price" },
  type: { ar: "النوع", en: "Type" },
  value: { ar: "القيمة", en: "Value" },
  visibility: { ar: "الظهور", en: "Visibility" },
  hidden: { ar: "مخفي في كل الفروع", en: "Hidden everywhere" },
  visibleInBranch: { ar: "ظاهر في الفرع", en: "Visible in branch" },
  branch: { ar: "الفرع", en: "Branch" },
  branches: { ar: "الفروع", en: "Branches" },
  lifecycle: { ar: "الحالة", en: "Status" },
  shortName: { ar: "الاسم المختصر", en: "Short name" },
  title: { ar: "العنوان", en: "Title" },
  options: { ar: "الخيارات", en: "Options" },
  min: { ar: "الحد الأدنى", en: "Min" },
  max: { ar: "الحد الأعلى", en: "Max" },
  isMandatory: { ar: "إلزامي", en: "Mandatory" },
  sectionIds: { ar: "ترتيب الأقسام", en: "Section order" },
  itemIds: { ar: "ترتيب الأصناف", en: "Item order" },
  hiddenItemOverrides: { ar: "أصناف كانت مخفية في الفرع", en: "Items hidden in the branch" },
  itemCount: { ar: "عدد الأصناف", en: "Item count" },
  copiedFrom: { ar: "منسوخ من", en: "Copied from" },
  menuName: { ar: "قائمة الطعام", en: "Menu" },
  restaurantName: { ar: "المطعم", en: "Restaurant" },
  restaurantCode: { ar: "رمز المطعم", en: "Restaurant code" },
  sectionName: { ar: "القسم", en: "Section" },
  itemName: { ar: "الصنف", en: "Item" },
  droppedAdditionLinks: { ar: "إضافات لم تُربط", en: "Additions not linked" },
  archivedAt: { ar: "تاريخ الأرشفة", en: "Archived at" },
  additionIds: { ar: "ترتيب الإضافات", en: "Addition order" },
  needsMenuReview: { ar: "بانتظار مراجعة (قديم)", en: "Needed review (legacy)" },
  lastMenuUpdateScope: { ar: "آخر جهة عدّلت (قديم)", en: "Last editor scope (legacy)" },
};

const HIDDEN_KEYS = new Set([
  "_id",
  "__v",
  "createdAt",
  "updatedAt",
  "hiddenAt",
  "hiddenBy",
  "version",
  "effectivePrice",
  "itemId",
  "sectionId",
  "menuId",
  "restaurantId",
]);

const BULK_OPERATIONS = {
  add: { ar: "إضافة خصم", en: "Add discount" },
  update: { ar: "تعديل خصم", en: "Update discount" },
  remove: { ar: "إزالة خصم", en: "Remove discount" },
  copy: { ar: "نسخ خصم", en: "Copy discount" },
};
const BULK_SCOPES = {
  items: { ar: "أصناف محددة", en: "Specific items" },
  sections: { ar: "أقسام", en: "Sections" },
  menu: { ar: "قائمة الطعام كاملة", en: "Entire menu" },
};

const nameOf = (value, k) => (value && typeof value === "object" ? value[k] || value.ar || value.en || "" : value || "");

// Where copied content came from (and went to). Copies between restaurants
// are flagged so the destination menu's history makes that obvious.
function CopyOrigin({ entry, isArabic }) {
  const k = isArabic ? "ar" : "en";
  const from = entry.changes?.copiedFrom || (entry.action === "discount_bulk" ? entry.changes?.source : null) || entry.before?.copiedFrom;
  if (!from) return null;
  const cross = entry.changes?.crossRestaurant || from.crossRestaurant;
  const parts = [from.restaurantName, from.menuName, nameOf(from.sectionName, k), nameOf(from.itemName, k)].filter(Boolean);
  if (!parts.length) return null;
  const skipped = (entry.changes?.additionLinks || []).filter((l) => l.status === "skipped").length;
  return (
    <p className="menu-history-reason">
      <strong>{cross ? (isArabic ? "منسوخ من مطعم آخر:" : "Copied from another restaurant:") : isArabic ? "منسوخ من:" : "Copied from:"}</strong> {parts.join(" › ")}
      {cross && entry.changes?.copiedTo?.restaurantName ? ` → ${entry.changes.copiedTo.restaurantName}` : ""}
      {skipped ? (isArabic ? ` · ${skipped} إضافة لم تُربط` : ` · ${skipped} addition(s) not linked`) : ""}
    </p>
  );
}

// One line describing a grouped bulk discount record.
function BulkSummary({ changes, isArabic }) {
  if (!changes) return null;
  const k = isArabic ? "ar" : "en";
  const discount = changes.discount ? (changes.discount.type === "percentage" ? `${changes.discount.value}%` : `${changes.discount.value}`) : "—";
  return (
    <p className="menu-history-reason">
      <strong>{BULK_OPERATIONS[changes.operation]?.[k] || changes.operation}</strong> · {(changes.pricingNames || []).join(", ")} · {changes.operation === "remove" ? "—" : discount} · {BULK_SCOPES[changes.scope]?.[k] || changes.scope} ·{" "}
      {isArabic ? `تغير ${changes.affectedCount} · مستثنى ${changes.skippedCount}` : `${changes.affectedCount} changed · ${changes.skippedCount} skipped`}
    </p>
  );
}

const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const normalizePrimitive = (value) => {
  if (value === undefined || value === null) return null;
  return value;
};

const getArrayIdentity = (item, index) => {
  if (!isPlainObject(item)) return `index:${index}`;

  const localizedName = item.name?.ar || item.name?.en;
  const additionId = item.addition?._id || item.addition;

  return String(
    item.branch ||
      item.branchId ||
      item._id ||
      additionId ||
      localizedName ||
      item.name ||
      item.order ||
      `index:${index}`,
  );
};

const getArrayDisplayLabel = (item, index, parentKey, isArabic) => {
  if (isPlainObject(item)) {
    if (parentKey === "addCosts" || parentKey === "pricing") {
      const name = item.name || `${index + 1}`;
      return isArabic ? `السعر (${name})` : `Price (${name})`;
    }

    if (parentKey === "items") {
      const name = item.name?.ar || item.name?.en || index + 1;
      return isArabic ? `الصنف (${name})` : `Item (${name})`;
    }

    if (parentKey === "hiddenBranches") {
      const name = item.branchName || item.branchId || index + 1;
      return isArabic ? `الفرع (${name})` : `Branch (${name})`;
    }

    if (parentKey === "additionGroups") {
      const name =
        item.addition?.title?.ar ||
        item.addition?.title?.en ||
        item.addition?.name?.ar ||
        item.addition?.name?.en ||
        item.addition?._id ||
        item.addition ||
        index + 1;
      return isArabic ? `مجموعة الإضافات (${name})` : `Addition group (${name})`;
    }
  }

  return isArabic ? `العنصر ${index + 1}` : `Item ${index + 1}`;
};

/**
 * Flattens any nested value into primitive leaf entries.
 * Arrays of objects are matched by a stable identity where possible instead
 * of being rendered as JSON blocks.
 */
const flattenValue = (value, path = [], labels = [], output = new Map()) => {
  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      const parentKey = path[path.length - 1];
      const identity = getArrayIdentity(item, index);
      const nextPath = [...path, `@${identity}`];
      const nextLabels = [...labels, { type: "array", parentKey, item, index }];
      flattenValue(item, nextPath, nextLabels, output);
    });

    if (value.length === 0) {
      output.set(path.join("."), { path, labels, value: [] });
    }

    return output;
  }

  if (isPlainObject(value)) {
    const visibleEntries = Object.entries(value).filter(
      ([key]) => !HIDDEN_KEYS.has(key),
    );

    if (visibleEntries.length === 0) {
      output.set(path.join("."), { path, labels, value: {} });
      return output;
    }

    visibleEntries.forEach(([key, nestedValue]) => {
      flattenValue(
        nestedValue,
        [...path, key],
        [...labels, { type: "field", key }],
        output,
      );
    });

    return output;
  }

  output.set(path.join("."), {
    path,
    labels,
    value: normalizePrimitive(value),
  });

  return output;
};

const areValuesEqual = (left, right) =>
  JSON.stringify(left) === JSON.stringify(right);

const getChangedFields = (before, after) => {
  const beforeMap = flattenValue(before);
  const afterMap = flattenValue(after);
  const keys = new Set([...beforeMap.keys(), ...afterMap.keys()]);

  return [...keys]
    .map((key) => {
      const beforeEntry = beforeMap.get(key);
      const afterEntry = afterMap.get(key);
      const beforeValue = beforeEntry?.value;
      const afterValue = afterEntry?.value;

      if (areValuesEqual(beforeValue, afterValue)) return null;

      return {
        key,
        path: afterEntry?.path || beforeEntry?.path || [],
        labels: afterEntry?.labels || beforeEntry?.labels || [],
        before: beforeValue,
        after: afterValue,
      };
    })
    .filter(Boolean);
};

const formatBoolean = (value, isArabic, path) => {
  const field = path[path.length - 1];

  if (field === "isShow") {
    return value
      ? isArabic
        ? "ظاهر"
        : "Visible"
      : isArabic
        ? "مخفي"
        : "Hidden";
  }

  if (field === "hiddenByStore") {
    return value ? (isArabic ? "نعم" : "Yes") : isArabic ? "لا" : "No";
  }

  return value ? (isArabic ? "نعم" : "Yes") : isArabic ? "لا" : "No";
};

const formatValue = (value, isArabic, path = []) => {
  if (value === undefined || value === null || value === "") return "—";
  if (typeof value === "boolean") return formatBoolean(value, isArabic, path);

  if (Array.isArray(value)) {
    return value.length ? value.map(String).join("، ") : isArabic ? "لا يوجد" : "None";
  }

  if (isPlainObject(value)) {
    const entries = Object.entries(value).filter(([key]) => !HIDDEN_KEYS.has(key));
    if (!entries.length) return isArabic ? "لا يوجد" : "None";
    return entries
      .map(([key, item]) => `${FIELD_LABELS[key]?.[isArabic ? "ar" : "en"] || key}: ${String(item ?? "—")}`)
      .join(isArabic ? "، " : ", ");
  }

  return String(value);
};

const getFieldLabel = (change, isArabic) => {
  const parts = [];

  change.labels.forEach((label) => {
    if (label.type === "array") {
      parts.push(
        getArrayDisplayLabel(
          label.item,
          label.index,
          label.parentKey,
          isArabic,
        ),
      );
      return;
    }

    if (label.type === "field") {
      const translated = FIELD_LABELS[label.key];
      parts.push(translated?.[isArabic ? "ar" : "en"] || label.key);
    }
  });

  // Remove duplicated parent + specialized array labels, e.g. "الأسعار - السعر (SAR)".
  return parts.filter((part, index) => part && part !== parts[index - 1]).join(
    isArabic ? " - " : " / ",
  );
};

export default function MenuHistoryModal({
  open,
  history = [],
  loading,
  onClose,
  // Paged mode (MENU_HISTORY_PAGINATION); all optional, so the unpaged callers render as before.
  hasMore = false,
  loadingMore = false,
  error = null,
  moreError = null,
  onLoadMore,
  onRetry,
}) {
  const { lang } = useContext(Context);
  const isArabic = lang === "ar";

  if (!open) return null;

  return (
    <div className="menu-history-backdrop" onClick={onClose}>
      <div
        className="menu-history-modal"
        dir={isArabic ? "rtl" : "ltr"}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="menu-history-header">
          <h3>{isArabic ? "سجل قائمة الطعام" : "Menu history"}</h3>
          <Button
            type="button"
            variant="warning"
            appearance="soft"
            size="sm"
            rounded
            icon={<CircleX size={17} />}
            onClick={onClose}
          />
        </div>

        {loading ? (
          <p>{isArabic ? "جاري التحميل..." : "Loading..."}</p>
        ) : error ? (
          <div className="menu-history-status" role="alert">
            <p>{isArabic ? "تعذر تحميل السجل" : "Could not load the history"}</p>
            {onRetry && (
              <Button type="button" size="sm" appearance="soft" onClick={onRetry}>
                {isArabic ? "إعادة المحاولة" : "Retry"}
              </Button>
            )}
          </div>
        ) : history.length === 0 ? (
          <p>{isArabic ? "لا يوجد سجل" : "No history"}</p>
        ) : (
          <div className="menu-history-list">
            {history.map((entry) => {
              const actionLabel =
                ACTION_LABELS[entry.action]?.[isArabic ? "ar" : "en"] ||
                entry.action;
              const changes = getChangedFields(entry.before, entry.after);
              const itemName =
                entry.itemName?.[isArabic ? "ar" : "en"] ||
                entry.itemName?.ar ||
                entry.itemName?.en ||
                entry.after?.name?.[isArabic ? "ar" : "en"] ||
                entry.before?.name?.[isArabic ? "ar" : "en"] ||
                entry.after?.name?.ar ||
                entry.before?.name?.ar ||
                "—";
              const sectionName =
                entry.sectionName?.[isArabic ? "ar" : "en"] ||
                entry.sectionName?.ar ||
                entry.sectionName?.en ||
                "—";

              return (
                <article className="menu-history-entry" key={entry._id}>
                  <div className="menu-history-meta">
                    <strong>{actionLabel}</strong>
                    <span>
                      {entry.actor?.name || "—"} (
                      {entry.actor?.side ? SIDE_NAMES[entry.actor.side]?.[isArabic ? "ar" : "en"] : entry.actor?.role || entry.actor?.scope || "—"})
                    </span>
                    <time>{new Date(entry.createdAt).toLocaleString()}</time>
                  </div>

                  <div className="menu-history-target">
                    <span>
                      <strong>{isArabic ? "الصنف:" : "Item:"}</strong>{" "}
                      {itemName}
                    </span>
                    <span>
                      <strong>{isArabic ? "القسم:" : "Section:"}</strong>{" "}
                      {sectionName}
                    </span>
                  </div>

                  {entry.actor?.branchName ? (
                    <p className="menu-history-branch">
                      {isArabic ? "الفرع:" : "Branch:"} {entry.actor.branchName}
                    </p>
                  ) : null}

                  {entry.action === "discount_bulk" && <BulkSummary changes={entry.changes} isArabic={isArabic} />}
                  {["item_copy", "section_copy", "discount_bulk"].includes(entry.action) && <CopyOrigin entry={entry} isArabic={isArabic} />}

                  {entry.reason ? (
                    <p className="menu-history-reason">
                      <strong>{isArabic ? "السبب:" : "Reason:"}</strong>{" "}
                      {entry.reason}
                    </p>
                  ) : null}

                  {changes.length === 0 ? (
                    <p className="menu-history-no-changes">
                      {isArabic
                        ? "لا توجد فروقات قابلة للعرض"
                        : "No displayable changes"}
                    </p>
                  ) : (
                    <div className="menu-history-changes">
                      {changes.map((change, index) => (
                        <div
                          className="menu-history-change-row"
                          key={`${change.key}-${index}`}
                        >
                          <strong className="menu-history-field-name">
                            {getFieldLabel(change, isArabic)}
                          </strong>

                          <div className="menu-history-values">
                            <div>
                              <b>{isArabic ? "قبل" : "Before"}</b>
                              <div className="menu-history-value-box">
                                {formatValue(
                                  change.before,
                                  isArabic,
                                  change.path,
                                )}
                              </div>
                            </div>

                            <div>
                              <b>{isArabic ? "بعد" : "After"}</b>
                              <div className="menu-history-value-box">
                                {formatValue(
                                  change.after,
                                  isArabic,
                                  change.path,
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}

        {!loading && !error && history.length > 0 && (hasMore || moreError) && (
          <div className="menu-history-status">
            {moreError && (
              <p role="alert">{isArabic ? "تعذر تحميل المزيد" : "Could not load older entries"}</p>
            )}
            <Button type="button" size="sm" appearance="soft" disabled={loadingMore} onClick={onLoadMore}>
              {loadingMore
                ? isArabic ? "جاري التحميل..." : "Loading..."
                : moreError
                  ? isArabic ? "إعادة المحاولة" : "Retry"
                  : isArabic ? "تحميل الأقدم" : "Load older"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
